/**
 * TurfPulse Wall Widget — `/widget`
 * ==================================
 *
 * Wall-mounted tablet / TV-mode dashboard for homeowners. Reads from one
 * server endpoint (`/api/widget/snapshot`) and re-polls every 5 min. Designed
 * to be glanceable from 6+ feet away — oversized numerals, calm dark palette,
 * 3 vertical zones (Today's Call → Agronomic Ticker → Upcoming).
 *
 * Auth: cookie-based. Mount once, refresh-token keeps the tablet alive 7d.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine } from 'recharts';
import { API_URL } from '../config/api';
import { WallHQQrPopover } from '../components/MountWallHQCard';
import { SuperHeadlinerHero } from '../components/SuperHeadlinerHero';
import { SmartHoldTile } from '../components/SmartHoldTile';

const REFRESH_MS = 5 * 60 * 1000; // 5 min

// ─── Formatters ─────────────────────────────────────────────
const fmt1 = (n) => (typeof n === 'number' ? n.toFixed(1) : '—');
const fmt0 = (n) => (typeof n === 'number' ? Math.round(n).toString() : '—');
const fmt2 = (n) => (typeof n === 'number' ? n.toFixed(2) : '—');

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const dt = new Date(iso);
    return dt.toLocaleString(undefined, {
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatClock(now) {
  return now.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatLongDate(now) {
  return now.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

// ─── Color tokens — solid emerald palette, no gradient mud ──
const COLORS = {
  safe:    { bg: 'bg-emerald-950/60',  border: 'border-emerald-500/40', text: 'text-emerald-300', accent: 'text-emerald-400' },
  caution: { bg: 'bg-amber-950/40',    border: 'border-amber-500/40',   text: 'text-amber-300',   accent: 'text-amber-400'   },
  no_spray:{ bg: 'bg-rose-950/40',     border: 'border-rose-500/40',    text: 'text-rose-300',    accent: 'text-rose-400'    },
  neutral: { bg: 'bg-zinc-900/80',     border: 'border-zinc-700/60',    text: 'text-zinc-300',    accent: 'text-zinc-100'    },
};

// ─── ZONE 4: FULL ECOWITT STATION READOUT ──────────────────
// Auto-hides when Ecowitt isn't configured. Fetches its own live data
// on a 2-min cadence (independent from the 5-min widget snapshot).
const ROLE_BY_CH = {
  1: { label: 'DRY', accent: 'text-rose-300', ring: 'border-rose-500/30', bg: 'bg-rose-950/20' },
  2: { label: 'AVG', accent: 'text-amber-300', ring: 'border-amber-500/30', bg: 'bg-amber-950/20' },
  3: { label: 'WET', accent: 'text-emerald-300', ring: 'border-emerald-500/30', bg: 'bg-emerald-950/20' },
};

const cardinal = (deg) => {
  if (deg === null || deg === undefined || isNaN(deg)) return '—';
  const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
  return dirs[Math.round(deg / 22.5) % 16];
};

const wh51Pct = (v) => {
  if (v === null || v === undefined) return null;
  return Math.max(0, Math.min(100, Math.round((v / 5) * 100)));
};

const fmtN = (v, digits = 1, fallback = '—') => {
  if (v === null || v === undefined || isNaN(v)) return fallback;
  return Number(v).toFixed(digits);
};

// ─── ZONE 1: Spray Window tile ──────────────────────────────
function SprayWindowTile({ data }) {
  if (!data) return null;
  const palette = COLORS[data.status] || COLORS.neutral;
  const labelByStatus = {
    safe: 'SPRAY WINDOW',
    caution: 'SPRAY WINDOW',
    no_spray: 'SPRAY WINDOW',
  };
  const dt = data.delta_t;
  const dp = data.dewpoint;
  return (
    <div
      className={`flex-1 rounded-2xl border ${palette.border} ${palette.bg} p-4 sm:p-5 backdrop-blur`}
      data-testid="widget-spray-tile"
    >
      <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
        {labelByStatus[data.status] || 'SPRAY WINDOW'}
      </div>
      <div className={`text-4xl sm:text-5xl font-bold ${palette.accent} mb-2`} data-testid="widget-spray-label">
        {data.label || '—'}
      </div>
      {data.status === 'safe' && data.closes_in_hours != null ? (
        <div className="text-sm sm:text-base text-zinc-300 mb-2">
          ~{data.closes_in_hours}h window before wind/heat closes it
        </div>
      ) : null}
      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs sm:text-sm text-zinc-400">
        <span>Wind <span className="tabular-nums text-zinc-200">{fmt0(data.wind_mph)} mph</span></span>
        <span>Now <span className="tabular-nums text-zinc-200">{fmt0(data.temp_f)}°F</span></span>
        <span>High <span className="tabular-nums text-zinc-200">{fmt0(data.high_f)}°F</span></span>
        {data.rain_today_in > 0 ? (
          <span>Rain <span className="tabular-nums text-zinc-200">{fmt2(data.rain_today_in)}"</span></span>
        ) : null}
      </div>
      {/* Super-grade sub-signals: Delta-T (spray quality) + Dewpoint (disease) */}
      {(dt || dp) && (
        <div className="mt-2 pt-2 border-t border-zinc-800/60 grid grid-cols-1 sm:grid-cols-2 gap-1 text-[11px] sm:text-xs" data-testid="widget-spray-subsignals">
          {dt && (
            <div>
              <span className="text-zinc-500 uppercase tracking-wider text-[9px]">Delta-T</span>{' '}
              <span className={`tabular-nums font-semibold ${dt.can_spray ? 'text-emerald-300' : 'text-rose-300'}`}>
                {dt.value_c}°C
              </span>
              <span className="text-zinc-400 ml-1">· {dt.label}</span>
            </div>
          )}
          {dp && (
            <div>
              <span className="text-zinc-500 uppercase tracking-wider text-[9px]">Dewpoint</span>{' '}
              <span className={`tabular-nums font-semibold ${
                dp.band === 'stuck_dew' ? 'text-rose-300'
                : dp.band === 'heavy' ? 'text-amber-300'
                : dp.band === 'moderate' ? 'text-yellow-300'
                : 'text-zinc-300'
              }`}>
                {dp.value_f}°F
              </span>
              <span className="text-zinc-400 ml-1">· {dp.label}</span>
            </div>
          )}
        </div>
      )}
      {data.reasons && data.reasons.length > 0 ? (
        <ul className="mt-1.5 text-[11px] text-zinc-500 space-y-0">
          {data.reasons.map((r, i) => <li key={i}>· {r}</li>)}
        </ul>
      ) : null}
    </div>
  );
}

// ─── ZONE 1: Irrigation tile ────────────────────────────────
function IrrigationTile({ data }) {
  if (!data) return null;
  const palette = COLORS.safe;
  const styleColor = {
    deep: 'text-emerald-400',
    light: 'text-sky-400',
    standard: 'text-zinc-200',
  }[data.next_run_style] || 'text-zinc-200';

  if (data.action === 'none_scheduled') {
    return (
      <div
        className={`flex-1 rounded-2xl border ${COLORS.neutral.border} ${COLORS.neutral.bg} p-4 sm:p-5 backdrop-blur`}
        data-testid="widget-irrigation-tile"
      >
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
          IRRIGATION
        </div>
        <div className="text-3xl sm:text-4xl font-bold text-zinc-300 mb-2">
          No schedule
        </div>
        <div className="text-xs text-zinc-500">
          {data.sensors_connected
            ? 'Create a Smart Recurring Schedule on /irrigation to automate.'
            : '🛡️ Guardian: connect Ecowitt sensors to enable auto-fire schedules.'}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex-1 rounded-2xl border ${palette.border} ${palette.bg} p-4 sm:p-5 backdrop-blur`}
      data-testid="widget-irrigation-tile"
    >
      <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
        NEXT IRRIGATION
      </div>
      <div className={`text-3xl sm:text-4xl font-bold ${styleColor} mb-1.5`} data-testid="widget-irrigation-when">
        {formatDateTime(data.next_run_at_iso)}
      </div>
      <div className="text-xl sm:text-2xl tabular-nums text-zinc-100 mb-2">
        <span data-testid="widget-irrigation-minutes">{data.next_run_minutes}</span>
        <span className="text-zinc-500 text-sm ml-2">min · {data.next_run_zones} zones</span>
      </div>
      {data.split_strategy === 'smart_gap' ? (
        <div className="inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
          ✨ Smart Split · {data.next_run_style?.toUpperCase()}
        </div>
      ) : (
        <div className="text-[11px] text-zinc-500">
          {data.schedule_name}
        </div>
      )}
      {!data.sensors_connected && (
        <div className="mt-2 inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[10px] font-medium bg-amber-500/15 text-amber-300 border border-amber-500/40" data-testid="widget-guardian-advisory">
          🛡️ ADVISORY · won't auto-fire without sensors
        </div>
      )}
    </div>
  );
}

// ─── ZONE 3: Upcoming rows ──────────────────────────────────
function UpcomingRow({ row }) {
  const iconByKind = {
    rain: '☔',
    light_rain: '🌦️',
    deep_water: '💧',
    light_water: '💦',
    irrigation: '🚿',
    gdd: '🌱',
  };
  const accentByKind = {
    rain: 'text-sky-300',
    light_rain: 'text-sky-400',
    deep_water: 'text-emerald-300',
    light_water: 'text-emerald-400',
    irrigation: 'text-emerald-300',
    gdd: 'text-zinc-300',
  };
  return (
    <div className="flex items-center gap-3 py-1.5 border-b border-zinc-800/60 last:border-0" data-testid="widget-upcoming-row">
      <div className="w-16">
        <div className="text-sm font-semibold text-zinc-200 leading-tight">{row.dow}</div>
        <div className="text-[10px] text-zinc-500 tabular-nums">{row.date_short}</div>
      </div>
      <div className="text-xl w-8 text-center" aria-hidden="true">
        {iconByKind[row.kind] || '•'}
      </div>
      <div className={`flex-1 text-sm ${accentByKind[row.kind] || 'text-zinc-300'} truncate`}>
        {row.headline}
      </div>
    </div>
  );
}

function UpcomingPanel({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 backdrop-blur px-4 py-3" data-testid="widget-upcoming">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Next 3 days</div>
      <div className="divide-y divide-zinc-800/60">
        {rows.map((r) => <UpcomingRow key={r.date_iso} row={r} />)}
      </div>
    </div>
  );
}

// ─── Top header ─────────────────────────────────────────────
function WidgetHeader({ data, clock }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
      <div>
        <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-emerald-500 mb-0.5">
          TurfPulse · Lawn HQ
        </div>
        <div className="text-xl sm:text-2xl font-semibold text-zinc-100" data-testid="widget-location">
          {data?.location?.name || 'Your lawn'}
        </div>
      </div>
      <div className="text-right">
        <div className="text-2xl sm:text-3xl font-semibold tabular-nums text-zinc-100" data-testid="widget-clock">
          {formatClock(clock)}
        </div>
        <div className="text-[10px] sm:text-xs text-zinc-500">
          {formatLongDate(clock)}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────
export default function WidgetPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clock, setClock] = useState(new Date());

  const fetchSnapshot = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/widget/snapshot`, {
        withCredentials: true,
      });
      setData(res.data);
      setError(res.data?.error || null);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError('not_authenticated');
      } else {
        setError(e?.response?.data?.detail || e?.message || 'fetch_failed');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSnapshot();
    const id = setInterval(fetchSnapshot, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchSnapshot]);

  // Live clock — every 30s is enough, no need for per-second redraw
  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  // Auth-required state
  if (error === 'not_authenticated') {
    return (
      <div className="min-h-screen bg-black text-zinc-100 flex items-center justify-center p-8" data-testid="widget-auth-required">
        <div className="max-w-md text-center">
          <div className="text-emerald-500 text-xs uppercase tracking-[0.3em] mb-2">TurfPulse · Lawn HQ</div>
          <h1 className="text-3xl font-semibold mb-2">Sign in to mount this</h1>
          <p className="text-zinc-400 mb-6">
            The wall widget needs your account once. After that, it stays live for ~7 days
            on this device without re-login.
          </p>
          <Link
            to="/?next=/widget"
            className="inline-block bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-6 py-3 rounded-lg"
            data-testid="widget-signin-link"
          >
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  // Location-required state
  if (error === 'no_location') {
    return (
      <div className="min-h-screen bg-black text-zinc-100 flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <div className="text-emerald-500 text-xs uppercase tracking-[0.3em] mb-2">TurfPulse · Lawn HQ</div>
          <h1 className="text-3xl font-semibold mb-2">Set your location</h1>
          <p className="text-zinc-400 mb-6">
            The widget needs your property location to fetch local weather, GDD, and ET.
          </p>
          <Link
            to="/property"
            className="inline-block bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-6 py-3 rounded-lg"
          >
            Set location
          </Link>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading || !data) {
    return (
      <div className="min-h-screen bg-black text-zinc-500 flex items-center justify-center p-8">
        <div className="animate-pulse text-sm uppercase tracking-[0.3em]">Pulling lawn snapshot…</div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-black text-zinc-100 px-4 sm:px-6 py-4 sm:py-5 pb-20"
      style={{
        backgroundImage:
          'radial-gradient(circle at 50% 0%, rgba(16,185,129,0.08), transparent 55%), radial-gradient(circle at 50% 100%, rgba(16,185,129,0.04), transparent 60%)',
      }}
      data-testid="widget-page"
    >
      <div className="max-w-6xl mx-auto flex flex-col gap-4">
        <WidgetHeader data={data} clock={clock} />

        {/* ─── THIS WEEK'S CALL (dynamic hero — picks dominant agronomic story) ─── */}
        <SuperHeadlinerHero />

        {/* ─── TODAY'S CALL (spray + irrigation) ─── */}
        <Section label="Today's call">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SprayWindowTile data={data?.today?.spray_window} />
            <IrrigationTile data={data?.today?.irrigation} />
          </div>
          {/* Smart Hold recommendation — auto-derived from MAD + dry-down slope */}
          <div className="mt-3">
            <SmartHoldTile />
          </div>
        </Section>

        {/* ─── GROUND TRUTH (soil triad — hero of the page) ─── */}
        <GroundTruthSection data={data} />

        {/* ─── SOIL EC (WH52 3-in-1 probes) — auto-hides if no EC data ─── */}
        <SoilECDashboard />

        {/* ─── FULL STATION (every Ecowitt readout, no scroll) ─── */}
        <FullStationDashboard />

        {/* ─── DEPLETION CURVE (paid: 7d/90d/365d sparkline + dry-down slope) ─── */}
        <DepletionCurve />

        {/* ─── COCKPIT VITALS ticker ─── */}
        <FooterTicker ticker={data?.ticker} />

        {/* ─── NEXT 3 DAYS (one-liner per day) ─── */}
        <Section label="Next 3 days">
          <UpcomingPanel rows={data?.upcoming} />
        </Section>

        {/* Meta */}
        <div className="flex justify-between items-center text-[10px] text-zinc-600 pt-1">
          <span>Refreshes every 5 min · {data?.grass_type || ''}</span>
          <div className="flex items-center gap-3">
            <WallHQQrPopover />
            <span className="tabular-nums">
              Updated {data?.generated_at ? new Date(data.generated_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* ─── BOTTOM MARQUEE — always-running station ticker ─── */}
      <StationMarquee />
    </div>
  );
}

// ─── Section wrapper — centered label + content ─────────────
function Section({ label, children }) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      {children}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-3 mb-2 px-1">
      <div className="h-px flex-1 bg-zinc-800" />
      <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 font-semibold whitespace-nowrap">
        {children}
      </div>
      <div className="h-px flex-1 bg-zinc-800" />
    </div>
  );
}

// ─── Ground Truth Section — soil triad (hero) ───────────────
function GroundTruthSection({ data }) {
  // Pull live ecowitt data (independent of snapshot)
  const [live, setLive] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/ecowitt/live`, { withCredentials: true });
      setLive(r.data);
    } catch {
      /* auto-hide on failure */
    }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 120 * 1000);
    return () => clearInterval(id);
  }, [load]);

  if (!live) return null;
  const soil = live.soil_channels || [];
  const temps = live.soil_temp_channels || [];
  const batteries = live.batteries || {};
  if (soil.length === 0) return null;

  const zones = [1, 2, 3].map((ch) => ({
    ch,
    role: ROLE_BY_CH[ch],
    moisture: soil.find((s) => s.channel === ch),
    temp: temps.find((s) => s.channel === ch),
    mBat: batteries[`soilmoisture_sensor_ch${ch}`],
    tBat: batteries[`temperature_sensor_ch${ch}`],
  }));

  return (
    <div>
      <SectionLabel>Ground truth · soil triad</SectionLabel>
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        {zones.map(({ ch, role, moisture, temp, mBat, tBat }) => (
          <div
            key={ch}
            data-testid={`widget-station-zone-ch${ch}`}
            className={`rounded-2xl border ${role.ring} ${role.bg} px-5 py-4 text-center backdrop-blur`}
          >
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className={`text-xs uppercase tracking-[0.3em] font-bold ${role.accent}`}>
                {role.label}
              </span>
              <span className="text-[9px] text-zinc-600 tracking-widest">· CH{ch}</span>
            </div>
            <div className="text-4xl sm:text-5xl font-bold text-zinc-100 tabular-nums leading-none">
              {fmtN(moisture?.moisture_pct, 0)}
              <span className="text-xl text-zinc-500 font-normal">%</span>
            </div>
            <div className="mt-1 text-sm text-zinc-400">
              Soil <span className="text-zinc-200 font-semibold tabular-nums">{fmtN(temp?.temp_f, 1)}°F</span>
            </div>
            {(mBat !== undefined || tBat !== undefined) && (
              <div className="mt-1.5 text-[9px] tracking-widest text-zinc-600 flex justify-center gap-3">
                {mBat !== undefined && <span className={wh51Pct(mBat) < 30 ? 'text-rose-400' : ''}>M {wh51Pct(mBat)}%</span>}
                {tBat !== undefined && <span className={wh51Pct(tBat) < 30 ? 'text-rose-400' : ''}>T {wh51Pct(tBat)}%</span>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Soil EC Dashboard — WH52 3-in-1 probes only ────────────
// Surfaces electrical conductivity (μS/cm) per channel with band tones.
// Auto-hides when no EC channels exist (legacy WH51-only setups).
// Bands: deficient <200 · ideal 200-1200 · watch 1200-2400 · high >2400.
const EC_BAND_TONES = {
  deficient: { ring: 'border-amber-700/60', bg: 'bg-amber-950/40', accent: 'text-amber-300', label: 'deficient' },
  ideal:     { ring: 'border-emerald-700/60', bg: 'bg-emerald-950/40', accent: 'text-emerald-300', label: 'ideal' },
  watch:     { ring: 'border-orange-700/60', bg: 'bg-orange-950/40', accent: 'text-orange-300', label: 'watch' },
  high:      { ring: 'border-rose-700/60', bg: 'bg-rose-950/40', accent: 'text-rose-300', label: 'high · flush' },
};

function SoilECDashboard() {
  const [live, setLive] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/ecowitt/live`, { withCredentials: true });
      setLive(r.data);
    } catch { /* auto-hide */ }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 120 * 1000);
    return () => clearInterval(id);
  }, [load]);

  if (!live) return null;
  const ec = live.soil_ec_channels || [];
  if (ec.length === 0) return null;  // legacy WH51-only — hide entirely

  return (
    <div data-testid="widget-soil-ec">
      <SectionLabel>Soil EC · WH52 (μS/cm)</SectionLabel>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
        {ec.map((c) => {
          const tone = EC_BAND_TONES[c.ec_band] || EC_BAND_TONES.ideal;
          return (
            <div
              key={`ec-${c.channel}`}
              data-testid={`widget-ec-ch${c.channel}`}
              className={`rounded-2xl border ${tone.ring} ${tone.bg} px-4 py-3 backdrop-blur`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-[10px] uppercase tracking-[0.3em] font-bold ${tone.accent}`}>
                  CH{c.channel}
                </span>
                <span className="text-[9px] text-zinc-500 uppercase tracking-widest">
                  {tone.label}
                </span>
              </div>
              <div className={`text-3xl sm:text-4xl font-bold tabular-nums leading-none ${tone.accent}`}>
                {c.ec_us_cm != null ? Math.round(c.ec_us_cm) : '—'}
                <span className="text-base text-zinc-500 font-normal ml-1">μS/cm</span>
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">
                soil salts · feeds nutrient call
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Full Station Dashboard — at-a-glance Ecowitt control center ─
// Surfaces every meaningful station readout at fixed positions so the
// wall display becomes a true HQ — no waiting for a marquee to scroll.
// 10-foot UI: 5xl numbers, color-coded thresholds, dense but readable.
function FullStationDashboard() {
  const [live, setLive] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/ecowitt/live`, { withCredentials: true });
      setLive(r.data);
    } catch { /* auto-hide */ }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 120 * 1000);
    return () => clearInterval(id);
  }, [load]);

  if (!live) return null;
  const w = live.weather || {};
  const wind = live.wind || {};
  const solar = live.solar || {};
  const pressure = live.pressure || {};
  const indoor = live.indoor || {};
  const batteries = live.batteries || {};

  // Disease-pressure tone for dewpoint (≥65°F = leaf-wetness risk)
  const dewTone = (w.dewpoint_f ?? 0) >= 65 ? 'text-rose-300' : (w.dewpoint_f ?? 0) >= 60 ? 'text-amber-300' : 'text-sky-300';
  // Wind drift threshold (>10 mph = poor spray, >15 = no-go)
  const windTone = (wind.wind_mph ?? 0) >= 15 ? 'text-rose-300' : (wind.wind_mph ?? 0) >= 10 ? 'text-amber-300' : 'text-emerald-300';
  // UV protection threshold
  const uvTone = (solar.uv_index ?? 0) >= 8 ? 'text-rose-300' : (solar.uv_index ?? 0) >= 6 ? 'text-amber-300' : 'text-zinc-200';
  // Rain rate active
  const rainActive = (w.rain_rate_in_hr ?? 0) > 0;

  const Tile = ({ label, value, valueClass = 'text-zinc-100', sub, subClass = 'text-zinc-500', testId }) => (
    <div
      className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-4 py-3 backdrop-blur"
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">{label}</div>
      <div className={`text-3xl sm:text-4xl font-bold tabular-nums leading-none mt-1 ${valueClass}`}>
        {value}
      </div>
      {sub !== undefined && (
        <div className={`text-xs mt-1.5 tabular-nums ${subClass}`}>{sub}</div>
      )}
    </div>
  );

  // Battery rows — show all WH51 / WN34 / WS90 batteries
  const battRows = [
    ...[1, 2, 3].map((ch) => ({
      label: `WH51·CH${ch}`,
      pct: wh51Pct(batteries[`soilmoisture_sensor_ch${ch}`]),
    })),
    ...[1, 2, 3].map((ch) => ({
      label: `WN34·CH${ch}`,
      pct: wh51Pct(batteries[`temperature_sensor_ch${ch}`]),
    })),
    {
      label: 'WS90 batt',
      pct: batteries.haptic_array_battery !== undefined
        ? Math.max(0, Math.min(100, Math.round(((batteries.haptic_array_battery - 2.0) / (3.0 - 2.0)) * 100)))
        : null,
    },
    {
      label: 'WS90 cap',
      pct: batteries.haptic_array_capacitor !== undefined
        ? Math.max(0, Math.min(100, Math.round(((batteries.haptic_array_capacitor - 2.0) / (3.0 - 2.0)) * 100)))
        : null,
    },
  ];

  return (
    <div data-testid="widget-full-station">
      <SectionLabel>Full station · all Ecowitt readouts</SectionLabel>

      {/* Row 1: Atmosphere · Sky · Wind · Rain — 4 columns of big tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        {/* Atmosphere */}
        <Tile
          testId="widget-tile-air"
          label="Air"
          value={<>{fmtN(w.temp_f, 1)}<span className="text-xl text-zinc-500 font-normal">°F</span></>}
          sub={`feels ${fmtN(w.temp_feels_like_f, 0)}° · RH ${fmtN(w.rh_pct, 0)}%`}
        />
        <Tile
          testId="widget-tile-dewpoint"
          label="Dewpoint"
          valueClass={dewTone}
          value={<>{fmtN(w.dewpoint_f, 1)}<span className="text-xl text-zinc-500 font-normal">°F</span></>}
          sub={(w.dewpoint_f ?? 0) >= 65 ? 'leaf-wetness risk' : (w.dewpoint_f ?? 0) >= 60 ? 'watch · disease' : 'comfort band'}
          subClass={(w.dewpoint_f ?? 0) >= 65 ? 'text-rose-400' : (w.dewpoint_f ?? 0) >= 60 ? 'text-amber-400' : 'text-zinc-500'}
        />
        {/* Sky */}
        <Tile
          testId="widget-tile-solar"
          label="Solar"
          valueClass="text-amber-300"
          value={<>{fmtN(solar.solar_wm2, 0)}<span className="text-xl text-zinc-500 font-normal">W/m²</span></>}
          sub={<>UV <span className={uvTone}>{fmtN(solar.uv_index, 1)}</span> · {(solar.uv_index ?? 0) >= 8 ? 'extreme' : (solar.uv_index ?? 0) >= 6 ? 'high' : 'moderate'}</>}
        />
        <Tile
          testId="widget-tile-pressure"
          label="Pressure"
          valueClass="text-violet-300"
          value={<>{fmtN(pressure.relative_in_hg, 2)}<span className="text-xl text-zinc-500 font-normal">″Hg</span></>}
          sub={`abs ${fmtN(pressure.absolute_in_hg, 2)}″`}
        />
      </div>

      {/* Row 2: Wind · Rain — 2-up wider tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mt-2 sm:mt-3">
        <Tile
          testId="widget-tile-wind"
          label="Wind"
          valueClass={windTone}
          value={<>{fmtN(wind.wind_mph, 1)}<span className="text-xl text-zinc-500 font-normal"> mph {cardinal(wind.wind_dir_deg)}</span></>}
          sub={`gust ${fmtN(wind.wind_gust_mph, 1)} · today max ${fmtN(wind.wind_daily_max_mph, 1)} mph`}
        />
        <Tile
          testId="widget-tile-rain-today"
          label="Rain today"
          valueClass={rainActive ? 'text-sky-200' : 'text-zinc-200'}
          value={<>{fmtN(w.rain_today_in, 2)}<span className="text-xl text-zinc-500 font-normal">″</span></>}
          sub={rainActive ? `↓ ${fmtN(w.rain_rate_in_hr, 2)}″/hr now` : `event ${fmtN(w.rain_event_in, 2)}″`}
          subClass={rainActive ? 'text-sky-300' : 'text-zinc-500'}
        />
        <Tile
          testId="widget-tile-rain-24h"
          label="Rain 24h"
          valueClass="text-sky-200"
          value={<>{fmtN(w.rain_24h_in, 2)}<span className="text-xl text-zinc-500 font-normal">″</span></>}
          sub={`hourly ${fmtN(w.rain_hourly_in, 2)}″`}
        />
        <Tile
          testId="widget-tile-rain-period"
          label="Rain wk · mo"
          valueClass="text-sky-200"
          value={<>{fmtN(w.rain_weekly_in, 2)}<span className="text-xl text-zinc-500 font-normal">″</span></>}
          sub={`month ${fmtN(w.rain_monthly_in, 2)}″ · year ${fmtN(w.rain_yearly_in, 1)}″`}
        />
      </div>

      {/* Row 3: Indoor + Battery health */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3 mt-2 sm:mt-3">
        <Tile
          testId="widget-tile-indoor"
          label="Indoor / garage"
          value={<>{fmtN(indoor.temp_f, 0)}<span className="text-xl text-zinc-500 font-normal">°F</span></>}
          sub={`RH ${fmtN(indoor.rh_pct, 0)}%`}
        />
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-4 py-3 backdrop-blur lg:col-span-2" data-testid="widget-tile-batteries">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">Sensor batteries</div>
          <div className="grid grid-cols-4 gap-2 mt-2">
            {battRows.map((b, i) => {
              const lo = b.pct !== null && b.pct < 30;
              const mid = b.pct !== null && b.pct < 60;
              const tone = lo ? 'text-rose-300' : mid ? 'text-amber-300' : 'text-emerald-300';
              return (
                <div key={i} className="text-center" data-testid={`widget-battery-${b.label.toLowerCase().replace(/\W+/g, '-')}`}>
                  <div className="text-[9px] tracking-widest text-zinc-600 uppercase">{b.label}</div>
                  <div className={`text-xl font-bold tabular-nums leading-none mt-0.5 ${tone}`}>
                    {b.pct !== null ? `${b.pct}%` : '—'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Depletion Curve — paid: 7d/90d/365d soil moisture sparkline ──
// Reads from /api/ecowitt/history. Shows last 14d of CH1/CH2/CH3 with
// per-channel dry-down slope. Auto-hides for free users (or shows a
// quiet "Your last 7 days are being captured — upgrade to view" prompt).
function DepletionCurve() {
  const [hist, setHist] = useState(null);
  const [stats, setStats] = useState(null);
  const [anchors, setAnchors] = useState(null);
  const [gated, setGated] = useState(null);  // 402 detail when free user
  const [viewMode, setViewMode] = useState('full');  // 'full' | 'anchors'

  const load = useCallback(async () => {
    try {
      const [h, s, a] = await Promise.all([
        axios.get(`${API_URL}/api/ecowitt/history?days=14`, { withCredentials: true }),
        axios.get(`${API_URL}/api/ecowitt/history/stats?days=7`, { withCredentials: true }),
        axios.get(`${API_URL}/api/ecowitt/history/anchors?days=14`, { withCredentials: true }),
      ]);
      setHist(h.data);
      setStats(s.data);
      setAnchors(a.data);
      setGated(null);
    } catch (e) {
      if (e?.response?.status === 402) {
        setGated(e.response.data?.detail || { message: 'Paid feature' });
      }
      // 401 / network — auto-hide silently
    }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  // Free-tier upgrade nudge
  if (gated) {
    return (
      <div data-testid="widget-depletion-curve-locked">
        <SectionLabel>Depletion curve · paid</SectionLabel>
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-5 py-4 text-center">
          <div className="text-sm text-zinc-200 font-semibold">{gated.hint || gated.message}</div>
          <div className="text-[11px] text-zinc-500 mt-1">
            Free tier captures {gated.free_capture_window_days || 7}d of moisture data — upgrade to see the curve and dry-down slopes.
          </div>
          <Link to="/subscription" className="inline-block mt-3 text-xs uppercase tracking-widest text-amber-300 hover:text-amber-200">
            Upgrade →
          </Link>
        </div>
      </div>
    );
  }

  if (!hist || !hist.readings || hist.readings.length < 3) return null;

  // Anchor-mode: 2 cleanest points per day (predawn + predusk only)
  // Full-mode:  every 15-min reading, no filtering
  const isAnchorMode = viewMode === 'anchors' && anchors?.anchors?.length >= 2;
  const sourceRows = isAnchorMode ? anchors.anchors : hist.readings;
  // Recharts wants epoch ms for proper time-axis spacing
  const data = sourceRows.map((r) => ({
    t: new Date(r.ts).getTime(),
    ch1: r.ch1,
    ch2: r.ch2,
    ch3: r.ch3,
    canonical_window: r.canonical_window,
  }));

  const channels = [
    { key: 'ch1', label: 'DRY · CH1', color: '#fb7185' },    // rose-400
    { key: 'ch2', label: 'AVG · CH2', color: '#fbbf24' },    // amber-400
    { key: 'ch3', label: 'WET · CH3', color: '#60a5fa' },    // sky-400
  ];

  // Stats lookup by channel
  const statsBy = {};
  (stats?.stats || []).forEach((s) => { statsBy[s.channel] = s; });

  // In anchor mode, compute the average daily depletion per channel from the
  // server-side daily_depletion array — clean signal for slope tuning.
  const dailyAvgDepletion = {};
  if (isAnchorMode && anchors?.daily_depletion?.length) {
    [1, 2, 3].forEach((ch) => {
      const vals = anchors.daily_depletion
        .map((d) => d[`ch${ch}_depletion`])
        .filter((v) => typeof v === 'number');
      if (vals.length) {
        dailyAvgDepletion[ch] = +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
      }
    });
  }

  return (
    <div data-testid="widget-depletion-curve">
      <SectionLabel>
        Depletion curve · {isAnchorMode
          ? `${anchors.complete_days} canonical days · 5:30 AM ↔ 6:30 PM anchors`
          : `last ${hist.window_days}d · ${hist.point_count} points (15-min)`}
      </SectionLabel>

      {/* View-mode toggle — switches between full 15-min stream and 2-anchor/day */}
      <div className="flex items-center gap-1 mb-2" data-testid="widget-curve-view-toggle">
        <button
          onClick={() => setViewMode('full')}
          className={`text-[10px] tracking-widest uppercase px-3 py-1.5 rounded font-semibold transition-colors ${
            viewMode === 'full'
              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
              : 'bg-zinc-900/60 text-zinc-500 border border-zinc-800 hover:text-zinc-300'
          }`}
          data-testid="widget-curve-view-full"
        >
          Full 15-min stream
        </button>
        <button
          onClick={() => setViewMode('anchors')}
          disabled={!anchors?.anchors?.length}
          className={`text-[10px] tracking-widest uppercase px-3 py-1.5 rounded font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            viewMode === 'anchors'
              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
              : 'bg-zinc-900/60 text-zinc-500 border border-zinc-800 hover:text-zinc-300'
          }`}
          data-testid="widget-curve-view-anchors"
        >
          ✨ Daily anchors only
          {anchors?.anchors?.length ? ` (${anchors.complete_days}d)` : ''}
        </button>
      </div>

      {/* Per-channel slope headlines — switches metric depending on view mode */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-2">
        {channels.map((c, i) => {
          const ch = i + 1;
          const s = statsBy[ch];
          const dailyDep = dailyAvgDepletion[ch];
          const slope = isAnchorMode ? dailyDep : s?.slope_pct_per_day;
          const slopeTone = slope === undefined || slope === null ? 'text-zinc-500'
            : isAnchorMode
              ? (slope > 5 ? 'text-rose-300' : slope > 2 ? 'text-amber-300' : 'text-emerald-300')
              : (slope < -1.0 ? 'text-rose-300' : slope < 0 ? 'text-amber-300' : 'text-emerald-300');
          return (
            <div
              key={c.key}
              className="rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-2 backdrop-blur"
              data-testid={`widget-curve-stats-ch${ch}`}
            >
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: c.color }} />
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{c.label}</span>
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-bold tabular-nums text-zinc-100">
                  {s?.last_pct ?? '—'}<span className="text-xs text-zinc-500 font-normal">%</span>
                </span>
                <span className={`text-xs font-mono tabular-nums ${slopeTone}`}>
                  {slope === undefined || slope === null ? '' :
                    isAnchorMode
                      ? `${slope > 0 ? '−' : '+'}${Math.abs(slope)}%/day depletion`
                      : `${slope > 0 ? '+' : ''}${slope}%/d`}
                </span>
              </div>
              <div className="text-[9px] text-zinc-600 tracking-widest mt-0.5">
                {isAnchorMode
                  ? `predawn → predusk · ${anchors.complete_days}d avg`
                  : `7d ${s?.min_pct ?? '—'}–${s?.max_pct ?? '—'}% · avg ${s?.avg_pct ?? '—'}%`}
              </div>
            </div>
          );
        })}
      </div>

      {/* Multi-line chart */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-3 py-3 backdrop-blur">
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <XAxis
              dataKey="t"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(t) => new Date(t).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
              tick={{ fill: '#71717a', fontSize: 10 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={{ stroke: '#27272a' }}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fill: '#71717a', fontSize: 10 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={{ stroke: '#27272a' }}
              ticks={[0, 25, 50, 75, 100]}
              tickFormatter={(v) => `${v}%`}
              width={40}
            />
            <Tooltip
              contentStyle={{ background: '#09090b', border: '1px solid #27272a', borderRadius: 8, fontSize: 11 }}
              labelFormatter={(t) => new Date(t).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              formatter={(v, name) => [`${v}%`, name?.toString().toUpperCase()]}
            />
            <ReferenceLine y={50} stroke="#3f3f46" strokeDasharray="2 2" />
            {channels.map((c) => (
              <Line
                key={c.key}
                type="monotone"
                dataKey={c.key}
                stroke={c.color}
                strokeWidth={isAnchorMode ? 2.5 : 2}
                dot={isAnchorMode ? { r: 3, fill: c.color } : false}
                connectNulls
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
        <div className="text-[9px] text-zinc-600 tracking-widest mt-1.5 text-center">
          {isAnchorMode
            ? `Anchored to 5:30 AM + 6:30 PM local · cleanest signal for slope tuning · TTL ${hist.retention_days}d`
            : `Captured every 15 min · TTL ${hist.retention_days}d (${hist.tier})`}
        </div>
      </div>
    </div>
  );
}

// ─── Station Marquee — always-running stock-ticker style strip ─
// Fixed at bottom of page, scrolls left horizontally forever. Houses
// every Ecowitt readout that doesn't fit in the centered cockpit above.
function StationMarquee() {
  const [live, setLive] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/ecowitt/live`, { withCredentials: true });
      setLive(r.data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 120 * 1000);
    return () => clearInterval(id);
  }, [load]);

  if (!live) return null;
  const w = live.weather || {};
  const wind = live.wind || {};
  const solar = live.solar || {};
  const pressure = live.pressure || {};
  const indoor = live.indoor || {};
  const batteries = live.batteries || {};

  // Build ticker items — pro metrics to cycle through
  const items = [
    { k: 'Outdoor',      v: `${fmtN(w.temp_f, 1)}°F`,                       sub: `feels ${fmtN(w.temp_feels_like_f, 0)}°`, c: 'text-zinc-100' },
    { k: 'Humidity',     v: `${fmtN(w.rh_pct, 0)}%`,                        sub: `dew ${fmtN(w.dewpoint_f, 0)}°F`,         c: 'text-sky-300' },
    { k: 'Solar',        v: `${fmtN(solar.solar_wm2, 0)} W/m²`,             sub: `UV ${fmtN(solar.uv_index, 1)}`,          c: 'text-amber-300' },
    { k: 'Pressure',     v: `${fmtN(pressure.relative_in_hg, 2)} inHg`,     sub: `abs ${fmtN(pressure.absolute_in_hg, 2)}`, c: 'text-violet-300' },
    { k: 'Wind',         v: `${fmtN(wind.wind_mph, 1)} mph ${cardinal(wind.wind_dir_deg)}`, sub: `gust ${fmtN(wind.wind_gust_mph, 1)}`, c: 'text-emerald-300' },
    { k: 'Rain today',   v: `${fmtN(w.rain_today_in, 2)}"`,                 sub: `rate ${fmtN(w.rain_rate_in_hr, 2)}"/hr`,  c: 'text-sky-200' },
    { k: 'Rain week',    v: `${fmtN(w.rain_weekly_in, 2)}"`,                sub: `month ${fmtN(w.rain_monthly_in, 2)}"`,   c: 'text-sky-200' },
    { k: 'Garage',       v: `${fmtN(indoor.temp_f, 0)}°F`,                  sub: `${fmtN(indoor.rh_pct, 0)}% RH`,          c: 'text-zinc-300' },
    ...[1, 2, 3].map((ch) => ({
      k: `WH51 CH${ch}`,
      v: `${wh51Pct(batteries[`soilmoisture_sensor_ch${ch}`]) ?? '—'}%`,
      sub: 'moist batt',
      c: (wh51Pct(batteries[`soilmoisture_sensor_ch${ch}`]) ?? 100) < 30 ? 'text-rose-400' : 'text-zinc-400',
    })),
    ...[1, 2, 3].map((ch) => ({
      k: `WN34 CH${ch}`,
      v: `${wh51Pct(batteries[`temperature_sensor_ch${ch}`]) ?? '—'}%`,
      sub: 'temp batt',
      c: (wh51Pct(batteries[`temperature_sensor_ch${ch}`]) ?? 100) < 30 ? 'text-rose-400' : 'text-zinc-400',
    })),
    { k: 'WS90 batt',    v: `${fmtN(batteries.haptic_array_battery, 2)}v`,  sub: batteries.haptic_array_battery > 2.5 ? 'good' : 'check', c: batteries.haptic_array_battery > 2.5 ? 'text-emerald-400' : 'text-rose-400' },
    { k: 'WS90 cap',     v: `${fmtN(batteries.haptic_array_capacitor, 2)}v`, sub: 'supercap',                              c: 'text-zinc-400' },
  ];

  // Duplicate so the loop appears seamless
  const loop = [...items, ...items];

  return (
    <>
      <style>{`
        @keyframes tp-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .tp-marquee-track {
          animation: tp-marquee 90s linear infinite;
          will-change: transform;
        }
      `}</style>
      <div
        className="fixed bottom-0 left-0 right-0 z-10 bg-zinc-950/95 border-t border-emerald-500/20 backdrop-blur overflow-hidden"
        data-testid="widget-marquee"
      >
        <div className="flex items-center">
          {/* Sticky left label */}
          <div className="shrink-0 px-5 py-3 border-r border-zinc-800 bg-black/60">
            <div className="text-xs uppercase tracking-[0.3em] text-emerald-500 font-bold">
              · Live
            </div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Station
            </div>
          </div>
          {/* Scrolling track */}
          <div className="flex-1 overflow-hidden relative">
            <div className="tp-marquee-track flex gap-10 py-3 whitespace-nowrap">
              {loop.map((item, i) => (
                <div key={i} className="flex items-baseline gap-3 shrink-0">
                  <span className="text-xs uppercase tracking-widest text-zinc-500">{item.k}</span>
                  <span className={`text-2xl font-bold tabular-nums ${item.c}`}>{item.v}</span>
                  <span className="text-sm text-zinc-500">· {item.sub}</span>
                  <span className="text-zinc-700 ml-3 text-base">◆</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Footer Ticker — cockpit vitals, centered ───────────────
function FooterTicker({ ticker }) {
  if (!ticker) return null;
  const ds = ticker.dollar_spot_pct;
  const dsColor = ds >= 50 ? 'text-rose-400' : ds >= 35 ? 'text-amber-400' : ds >= 20 ? 'text-yellow-300' : 'text-zinc-300';
  const isLiveTemp = ticker.soil_temp_source === 'ecowitt_live';
  const moistureAvg = ticker.soil_moisture_avg_pct;
  const moistureBand = ticker.soil_moisture_band;
  const moistureColor =
    moistureBand === 'dry' ? 'text-rose-400'
    : moistureBand === 'low' ? 'text-amber-300'
    : moistureBand === 'optimal' ? 'text-emerald-300'
    : moistureBand === 'wet' ? 'text-sky-300'
    : moistureBand === 'saturated' ? 'text-indigo-300'
    : 'text-zinc-300';

  const vitals = [
    { label: 'GDD today', value: `+${fmt0(ticker.gdd_today)}`, sub: `base ${ticker.gdd_base_temp_f}°F`, color: 'text-emerald-300' },
    { label: 'GDD 5-day', value: fmt0(ticker.gdd_past_5d),     sub: 'trailing',                         color: 'text-emerald-400' },
    { label: isLiveTemp ? 'Soil temp · live' : 'Soil temp',    value: `${fmt1(ticker.soil_temp_root_f)}°`, sub: ticker.soil_temp_depth_label, color: 'text-orange-300' },
    ...(moistureAvg !== undefined && moistureAvg !== null ? [{
      label: 'Soil moist · avg',
      value: `${fmt0(moistureAvg)}%`,
      sub: String(moistureBand || '').toUpperCase(),
      color: moistureColor,
    }] : []),
    { label: 'MAD bucket',  value: `${fmt0(ticker?.mad?.depletion_pct)}%`, sub: ticker?.mad?.label || 'depleted', color: ticker?.mad?.label === 'BREACH' ? 'text-rose-400' : 'text-amber-300' },
    { label: 'ET₀ today',   value: `${fmt2(ticker.et0_today_in)}"`,   sub: 'water lost',  color: 'text-cyan-300' },
    { label: 'Dollar spot', value: `${fmt0(ds)}%`, sub: ds >= 50 ? 'HIGH' : ds >= 35 ? 'WATCH' : 'LOW', color: dsColor },
  ];

  return (
    <div className="rounded-2xl border border-emerald-500/20 bg-zinc-950/70 backdrop-blur overflow-hidden" data-testid="widget-ticker">
      <div className="grid grid-cols-4 sm:grid-cols-7 divide-x divide-zinc-800/80">
        {vitals.map((v) => (
          <div key={v.label} className="px-3 py-2.5 text-center">
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">{v.label}</div>
            <div className={`text-xl sm:text-2xl font-bold tabular-nums leading-tight ${v.color}`}>
              {v.value}
            </div>
            <div className="text-[9px] text-zinc-500 truncate">{v.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
